import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import { getUserMonthlyOccupation, mapActivitiesToOccupations } from "../../utils/occupation";

const CONTRACTS = {
  CDD: "CDD",
  CDI: "CDI",
  VIE: "VIE",
  INTERNSHIP: "INTERNSHIP",
  APPRENTICESHIP: "APPRENTICESHIP",
  FREELANCE: "FREELANCE",
  ME_KOHE_TE_PLOTE: "Me kohe te plote",
};

export default function MasterDashboard({ businessUnit }) {
  const [users, setUsers] = useState([]);
  const [occupations, setOccupations] = useState([]);

  // Generate 4 months starting from current month
  const months = [...Array(4)].map((_, i) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + i);
    return date;
  });

  useEffect(() => {
    loadData();
  }, [businessUnit]);

  async function loadData() {
    const query = {
      contract: [CONTRACTS.CDD, CONTRACTS.CDI, CONTRACTS.VIE, CONTRACTS.INTERNSHIP, CONTRACTS.APPRENTICESHIP, CONTRACTS.FREELANCE, CONTRACTS.ME_KOHE_TE_PLOTE],
      roles: ["user", "admin", "super_admin"],
      status: ["active"],
    };
    if (businessUnit._id !== "ALL") {
      query.business_unit_id = businessUnit._id;
    }

    const { data } = await api.post("/user/search", query);
    const filteredUsers = data.users.filter((user) => {
      if (user.payment_type === "DAILY") return user.contract === "FREELANCE";
      return true;
    });
    setUsers(filteredUsers);

    // Load forecasted activities scoped to these users
    const userIds = filteredUsers.map((u) => u._id);
    if (userIds.length > 0) {
      const dateFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const { data: activities } = await api.post("/activity/search", { userIds, has_forecast: true, dateFrom });
      setOccupations(mapActivitiesToOccupations(activities));
    } else {
      setOccupations([]);
    }
  }

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const getMonthOccupancy = (user, month) => {
    return getUserMonthlyOccupation(occupations, user._id, month);
  };

  const getWorkingDaysInMonth = (month) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    let workingDays = 0;

    for (let day = 1; day <= lastDay; day++) {
      const currentDate = new Date(year, monthIndex, day);
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  const getSortedUsersForMonth = (month) => {
    return [...users].sort((a, b) => {
      const aOccupancy = getMonthOccupancy(a, month);
      const bOccupancy = getMonthOccupancy(b, month);
      return aOccupancy - bOccupancy;
    });
  };

  const separateUsersByPaymentType = (usersList) => {
    const freelancers = usersList.filter(u => u.payment_type === "DAILY");
    const employees = usersList.filter(u => u.payment_type === "MONTHLY" || !u.payment_type);
    return { freelancers, employees };
  };

  return (
    <div className="space-y-4 p-4">
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200"></div>
        <div className="p-4">
          <div className="grid grid-cols-4 gap-4">
            {months.map((month, monthIndex) => {
              const sortedUsers = getSortedUsersForMonth(month);
              const { freelancers, employees } = separateUsersByPaymentType(sortedUsers);
              
              const employeeDaysAvailable = employees.reduce(
                (sum, user) => sum + getWorkingDaysInMonth(month) - (getMonthOccupancy(user, month) / 100) * getWorkingDaysInMonth(month),
                0,
              );
              
              const freelancerDaysAvailable = freelancers.reduce(
                (sum, user) => sum + getWorkingDaysInMonth(month) - (getMonthOccupancy(user, month) / 100) * getWorkingDaysInMonth(month),
                0,
              );
              
              const totalDaysAvailable = employeeDaysAvailable + freelancerDaysAvailable;
              
              const renderUser = (user) => {
                const occupancy = getMonthOccupancy(user, month);
                const workingDays = getWorkingDaysInMonth(month);
                const availableDays = Math.max(0, workingDays - (occupancy / 100) * workingDays);
                const isInactive = user.exit_date && new Date() > new Date(user.exit_date);
                const incoming = user.starting_date && new Date(user.starting_date) > month;

                if (!availableDays) return null;

                return (
                  <div
                    key={user._id}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      query.set("user_modal_id", user._id);
                      query.set("user_modal_tab", "activity");
                      navigate({ search: query.toString() });
                    }}>
                    <img
                      src={user.avatar || "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"}
                      alt={user.name}
                      className="h-8 w-8 rounded-full object-cover border border-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-500">{isInactive ? "Inactive" : incoming ? "Incoming" : `${availableDays.toFixed(1)} days available`}</div>
                    </div>
                  </div>
                );
              };
              
              return (
                <div key={monthIndex} className="space-y-3">
                  <div className="text-center pb-2 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900">{month.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</h4>
                    <div className="text-xs text-gray-500">{totalDaysAvailable.toFixed(1)} days available</div>
                    <div className="text-xs space-x-2 mt-1">
                      <span className="text-gray-700">Employees: {employeeDaysAvailable.toFixed(1)}d</span>
                      <span className="text-blue-700">Freelancers: {freelancerDaysAvailable.toFixed(1)}d</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {employees.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-gray-700 uppercase px-2 py-1 bg-gray-100 rounded">
                          Employees ({employees.filter(u => {
                            const availableDays = Math.max(0, getWorkingDaysInMonth(month) - (getMonthOccupancy(u, month) / 100) * getWorkingDaysInMonth(month));
                            return availableDays > 0;
                          }).length})
                        </div>
                        {employees.map(renderUser)}
                      </div>
                    )}
                    
                    {freelancers.length > 0 && (
                      <div className="space-y-1 mt-3">
                        <div className="text-xs font-semibold text-blue-700 uppercase px-2 py-1 bg-blue-50 rounded">
                          Freelancers Daily ({freelancers.filter(u => {
                            const availableDays = Math.max(0, getWorkingDaysInMonth(month) - (getMonthOccupancy(u, month) / 100) * getWorkingDaysInMonth(month));
                            return availableDays > 0;
                          }).length})
                        </div>
                        {freelancers.map(renderUser)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
